import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";
import { onUiChange, getUiState } from "@/lib/uiState";

// Sitewide Passport List invitation — premium, restrained, never pushy.
// Timing: ~9s AFTER meaningful engagement (scroll or first interaction).
// Frequency: dismissed → suppressed 14 days; subscribed → never again; max once per session.
// Yields to: Passport Planner, gallery lightbox, mobile menu, sticky CTA, active form fields,
// and stays away from /ReadyToBook, /admin and /login entirely.
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
const EXCLUDED_PATHS = ["/readytobook", "/admin", "/login"];
const lsGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* storage unavailable */ } };
const ssGet = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
const ssSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* storage unavailable */ } };

const field = "w-full bg-transparent border-b border-white/25 py-2.5 font-body text-ink placeholder-white/40 focus:border-gold focus:outline-none transition-colors text-sm";

export default function PassportPopup() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lifted, setLifted] = useState(false); // sticky CTA visible → sit above it
  const [form, setForm] = useState({ first_name: "", email: "", website: "" });
  const engagedAt = useRef(0);
  const timer = useRef(null);
  const restoreFocus = useRef(null);
  const closeRef = useRef(null);

  const isExcludedPath = (p) => EXCLUDED_PATHS.some((x) => p.toLowerCase().startsWith(x));
  const formFocused = () => {
    const el = document.activeElement;
    return el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
  };
  const eligible = () => {
    if (lsGet("pl_subscribed")) return false;
    const dismissed = Number(lsGet("pl_dismissed") || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_MS) return false;
    if (ssGet("pl_shown")) return false;
    if (isExcludedPath(location.pathname)) return false;
    const ui = getUiState();
    if (ui.menu || ui.overlay) return false;
    if (formFocused()) return false;
    return true;
  };

  const openPopup = () => {
    if (!eligible()) return;
    restoreFocus.current = document.activeElement;
    ssSet("pl_shown", "1");
    setVisible(true);
  };

  const closePopup = (remember = true) => {
    setVisible(false);
    if (remember) lsSet("pl_dismissed", String(Date.now()));
    requestAnimationFrame(() => restoreFocus.current?.focus?.());
  };

  // Engagement listener: first scroll (>120px) or interaction arms the 9s timer.
  useEffect(() => {
    const arm = () => {
      if (engagedAt.current) return;
      engagedAt.current = Date.now();
      timer.current = setTimeout(openPopup, 9000);
    };
    const onScroll = () => { if (window.scrollY > 120) arm(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointerdown", arm, { once: true });
    window.addEventListener("keydown", arm, { once: true });
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("pointerdown", arm); window.removeEventListener("keydown", arm); clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Route change while visible → quietly close (session flag prevents reopen this session).
  useEffect(() => { setVisible(false); }, [location.pathname]);

  // Yield to menu/overlays while visible; track sticky CTA for positioning.
  useEffect(() => onUiChange((s) => {
    setLifted(!!s.stickyCta);
    if ((s.menu || s.overlay) && visible) closePopup(false);
  }), [visible]);

  // Focus + Escape while open.
  useEffect(() => {
    if (!visible) return;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); closePopup(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please enter your email address."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/subscribers", { ...form, source_page: `popup:${location.pathname}`, campaign_source: "passport_popup" });
      track("join_passport_list", { source_page: "popup" });
      lsSet("pl_subscribed", "1");
      setDone(true);
      toast.success(data.message || "You're on the list.");
      setTimeout(() => setVisible(false), 2800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (!visible) return null;
  return (
    <div role="dialog" aria-label="Join the Passport List" data-testid="passport-popup"
      className={`fixed ${lifted ? "bottom-24" : "bottom-4"} inset-x-4 sm:inset-x-auto sm:right-6 sm:w-[380px] z-[65] border border-gold/40 bg-gradient-to-br from-[#12100a] to-noir p-7 shadow-2xl shadow-black/60`}>
      <button ref={closeRef} type="button" onClick={() => closePopup()} aria-label="Close Passport List invitation" data-testid="popup-close"
        className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-white/50 hover:text-gold transition-colors">
        <X size={18} />
      </button>
      {done ? (
        <div data-testid="popup-success" className="flex items-center gap-3 text-gold py-4">
          <span className="w-9 h-9 border border-gold/50 flex items-center justify-center shrink-0"><Check size={16} /></span>
          <span className="font-serif text-lg font-light">You're on the list. Watch your inbox.</span>
        </div>
      ) : (
        <>
          <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold mb-3">The Passport List</p>
          <p className="font-serif text-2xl font-light leading-tight">Where will your passport take you next?</p>
          <p className="font-body text-white/60 text-[13px] mt-2 font-light">First access to new Passport Experiences, curated trip opportunities and travel inspiration.</p>
          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="popup-form">
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" />
            <div>
              <label htmlFor="popup-first" className="block font-body text-[10px] uppercase tracking-[0.15em] text-white/45 mb-1">First Name</label>
              <input id="popup-first" data-testid="popup-first-name" className={field} value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="popup-email" className="block font-body text-[10px] uppercase tracking-[0.15em] text-white/45 mb-1">Email Address</label>
              <input id="popup-email" data-testid="popup-email" type="email" inputMode="email" autoComplete="email" className={field}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <button type="submit" data-testid="popup-submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 border border-gold bg-gold text-black px-6 py-3.5 font-body text-[11px] uppercase tracking-[0.2em] hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-50">
              {loading ? "Joining…" : "Join the Passport List"} <ArrowRight size={14} />
            </button>
            <p className="font-body text-[10px] text-white/40 font-light">Unsubscribe anytime. <Link to="/privacy" className="text-gold link-underline" onClick={() => closePopup(false)}>Privacy Policy</Link>.</p>
          </form>
        </>
      )}
    </div>
  );
}
